/** Same-origin JSON API client. Mutations carry the session CSRF token and an idempotency key (NFR-03). */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: { field?: string; message: string }[];
  recovery?: string;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: { field?: string; message: string }[];
  readonly recovery?: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? [];
    if (body.recovery) this.recovery = body.recovery;
  }
  fieldError(field: string) {
    return this.details.find((d) => d.field === field || d.field?.startsWith(`${field}.`))?.message;
  }
}

let csrfToken: string | null = null;
export const setCsrfToken = (t: string | null) => {
  csrfToken = t;
};

let onUnauthenticated: (() => void) | null = null;
export const setUnauthenticatedHandler = (fn: () => void) => {
  onUnauthenticated = fn;
};

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init: { form?: FormData } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') {
    if (csrfToken) headers['x-csrf-token'] = csrfToken;
    headers['idempotency-key'] = crypto.randomUUID();
  }
  let payload: BodyInit | undefined;
  if (init.form) payload = init.form;
  else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(`/api/v1${path}`, { method, headers, body: payload, credentials: 'same-origin' });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = new ApiError(
      res.status,
      json?.error ?? { code: 'internal', message: res.statusText || 'Request failed' },
    );
    if (res.status === 401 && onUnauthenticated) onUnauthenticated();
    throw err;
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, undefined, { form }),
};

/** Builds a query string, omitting empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null | string[]>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    u.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}
